import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getContrastColor, formatLocalDateTime } from './common/utils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './css/TimeTable.css';
import './css/Dialogs.css';
import { useAuth } from './common/AuthContext';
import { useTimeTable } from './contexts/TimeTableContext';
import SubjectAddDialog from './SubjectAddDialog';
import SubjectPalette from './SubjectPalette';
import DragPreview from './DragPreview';
import ConfirmationDialog from './ConfirmationDialog';
import MonthlyCalendar from './MonthlyCalendar';
import { authFetch } from './common/authFetch';
import { downloadImage, canvasToBlob } from './common/shareService';
import ShareDialog from './components/ShareDialog';
import './css/MonthlyCalendar.css';


function TimeTable() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [startTime, setStartTime] = useState(() => {
        const savedStartTime = localStorage.getItem('startTime');
        return savedStartTime ? parseInt(savedStartTime) : 6;
    });
    const [endTime, setEndTime] = useState(() => {
        const savedEndTime = localStorage.getItem('endTime');
        return savedEndTime ? parseInt(savedEndTime) : 26;
    });
    const { user } = useAuth();
    const { schedules,
        subjects,
        currentStartDay,
        setCurrentStartDay,
        // getMondayDate,
        // getCurrentStartDay,
        startWithMonday,
        setStartWithMonday,
        setCurrentStartDaywithToday,
        setSchedules,
        // fetchSchedule,
        fetchScheduleByDate,
        createSchedule,
        maxScheduleId,
        setMaxScheduleId,
        // imageinaryScheduleIds,
        // removedSchedules,
        setRemovedSchedules,
        deleteSchedule,
        categories,
        fetchCategories,
        fetchSubjects,
        createSubject,
        createCategory,
        updateSchedule,
        lastWeekSchedules,
        setLastWeekSchedules,
        fetchLastWeekSchedules,
        createTodoSchedule,
        toggleTodoCompleted
    } = useTimeTable();

    const [myStudents, setMyStudents] = useState([]);
    const [selectedStudentId, setSelectedStudentId] = useState('');

    useEffect(() => {
        const fetchMyStudents = async () => {
            if (!user) return;
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/observer/students`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` }
                });
                const data = await res.json();
                if (data.success) {
                    setMyStudents(data.students);
                }
            } catch (e) {
                console.error("Failed to fetch students", e);
            }
        };
        fetchMyStudents();
    }, [user]);

    // Get userId from URL for consistent usage
    const queryParams = new URLSearchParams(location.search);
    const targetUserId = queryParams.get('userId');
    const isReadOnly = !!targetUserId; // Observer mode: no editing allowed

    useEffect(() => {
        // const params = new URLSearchParams(location.search); // Redundant
        // const userId = params.get('userId'); // Redundant
        setSelectedStudentId(targetUserId || '');

        // Refetch data when URL changes
        console.log('TimeTable.js useEffect: Refetching data. targetUserId:', targetUserId);
        fetchSubjects(targetUserId);
        fetchCategories(targetUserId);
        fetchScheduleByDate(currentStartDay, targetUserId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, currentStartDay, user, targetUserId]);





    // Dynamic Height Calculation logic
    const [cellHeight, setCellHeight] = useState(16); // Default fallback
    const cellRef = React.useRef(null);
    const printRef = useRef(null);
    const [isPdfExporting, setIsPdfExporting] = useState(false);
    const [pdfPreview, setPdfPreview] = useState(null); // { imgDataUrl, pdfInstance, fileName }
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareData, setShareData] = useState({});

    const handleStudentChange = (e) => {
        const newUserId = e.target.value;
        if (newUserId) {
            navigate(`/timetable?userId=${newUserId}`);
        } else {
            navigate(`/timetable`);
        }
    };

    // UI States
    const [isSubjectPaletteOpen, setIsSubjectPaletteOpen] = useState(false);
    const [isSubjectAddDialogOpen, setIsSubjectAddDialogOpen] = useState(false);
    // const [scheduleDirtyFlag, setScheduleDirtyFlag] = useState(false); // Removed dirty flag

    // Drag and Drop State
    const [draggedSubjectInfo, setDraggedSubjectInfo] = useState(null);
    const [draggedSchedule, setDraggedSchedule] = useState(null); // For moving existing schedules
    const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dragDimensions, setDragDimensions] = useState(null);
    const [resizingSchedule, setResizingSchedule] = useState(null); // { schedule, direction, initialY, initialTime, initialDuration }

    // Context Menu State
    const [contextMenu, setContextMenu] = useState(null);
    const [editDialog, setEditDialog] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, schedule: null });
    const [editingScheduleId, setEditingScheduleId] = useState(null); // 터치 기기용 선택 상태
    const prevEditingIdRef = useRef(null); // 이전에 선택되었던 ID 추적용
    const schedulesRef = useRef(schedules); // 리사이징/드래그 로직용 최신 스케줄 참조

    // Todo dialog state
    const [todoDialog, setTodoDialog] = useState({ isOpen: false, dayIndex: null, dayDate: null });
    const [todoSubjectId, setTodoSubjectId] = useState('');
    const [todoDescription, setTodoDescription] = useState('');

    // Todo context menu state
    const [todoContextMenu, setTodoContextMenu] = useState(null);
    const [todoEditDialog, setTodoEditDialog] = useState(null);
    const [todoDeleteDialog, setTodoDeleteDialog] = useState({ isOpen: false, schedule: null });

    // Weekly todo drag state
    const [weeklyDragOverDay, setWeeklyDragOverDay] = useState(null);
    const weeklyDragTodoRef = useRef(null);

    // Weekly todo touch state (long-press + touch drag)
    const weeklyTouchTimerRef = useRef(null);
    const weeklyTouchStartRef = useRef(null);
    const weeklyTouchDragRef = useRef(null);
    const weeklyTouchDragElRef = useRef(null);
    const [weeklyTouchDragging, setWeeklyTouchDragging] = useState(false);

    // 더블탭 감지용 ref (iPad 컨텍스트 메뉴)
    const scheduleTouchRef = useRef({ time: 0, x: 0, y: 0, schedule: null });
    const lastTapRef = useRef({ time: 0, x: 0, y: 0 });

    // View mode state
    const [viewMode, setViewMode] = useState(() => {
        return localStorage.getItem('viewMode') || 'weekly';
    });
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });
    const [monthlySchedules, setMonthlySchedules] = useState([]);

    // cellHeight 측정 (viewMode 전환 시 재측정)
    useEffect(() => {
        const updateHeight = () => {
            if (cellRef.current) {
                const style = window.getComputedStyle(cellRef.current);
                const height = parseFloat(style.height);
                if (height > 0) {
                    setCellHeight(height);
                }
            }
        };

        // viewMode 전환 후 DOM 렌더 완료 시점에 재측정
        const timer = setTimeout(updateHeight, 50);

        window.addEventListener('resize', updateHeight);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', updateHeight);
        };
    }, [viewMode]);

    // Actual sessions overlay state
    const [showActualOverlay, setShowActualOverlay] = useState(false);
    const [actualSessions, setActualSessions] = useState([]);

    useEffect(() => {
        schedulesRef.current = schedules;
    }, [schedules]);

    // Fetch actual sessions for overlay
    useEffect(() => {
        if (!showActualOverlay || !user) return;
        const fetchActualSessions = async () => {
            try {
                const startDate = new Date(currentStartDay);
                const endDate = new Date(currentStartDay);
                endDate.setDate(endDate.getDate() + 7);
                const userIdParam = targetUserId ? `&userId=${targetUserId}` : '';
                const response = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/v1/study_sessions?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}${userIdParam}`,
                    { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } }
                );
                const data = await response.json();
                if (data.success) {
                    setActualSessions(data.sessions || []);
                }
            } catch (error) {
                console.error('Error fetching actual sessions:', error);
            }
        };
        fetchActualSessions();
    }, [showActualOverlay, currentStartDay, user, targetUserId]);

    // Build overlay map: map actual sessions to grid cells
    const actualSessionMap = useMemo(() => {
        if (!showActualOverlay || actualSessions.length === 0) return {};
        const map = {};
        actualSessions.forEach(session => {
            const sessionStart = new Date(session.start_time);
            const weekStart = new Date(currentStartDay);
            const dayDiff = Math.floor((sessionStart - weekStart) / (1000 * 60 * 60 * 24));
            if (dayDiff < 0 || dayDiff >= 7) return;

            const hour = sessionStart.getHours();
            const minute = sessionStart.getMinutes();
            const startRow = (hour - (startTime > 24 ? startTime - 24 : startTime)) * 2 + (minute >= 30 ? 1 : 0);
            if (startRow < 0) return;

            const cellKey = `${dayDiff}-${startRow}`;
            if (!map[cellKey]) map[cellKey] = [];
            map[cellKey].push(session);
        });
        return map;
    }, [showActualOverlay, actualSessions, currentStartDay, startTime]);

    useEffect(() => {
        // Close context menu and clear selection on global click
        const handleClick = (e) => {
            setContextMenu(null);
            setTodoContextMenu(null);
        };
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, []);

    // Flush all modified schedules to server
    const flushPendingSchedules = useCallback(() => {
        const pending = schedulesRef.current.filter(s => s.modified);
        pending.forEach(s => {
            if (s.schedule_id !== -1) {
                updateSchedule(s);
            } else {
                createSchedule(s);
            }
        });
        if (pending.length > 0) {
            setSchedules(prev => prev.map(s => s.modified ? { ...s, modified: false } : s));
        }
    }, [updateSchedule, createSchedule, setSchedules]);

    // Deferred Save Logic: Save when selection changes
    useEffect(() => {
        const oldId = prevEditingIdRef.current;
        const newId = editingScheduleId;

        if (oldId !== newId && oldId !== null) {
            // Find the previously selected schedule to see if it was modified
            const prevSchedule = schedules.find(s => {
                const sId = s.schedule_id !== -1 ? s.schedule_id : `img-${s.imageinary_schedule_id}`;
                return sId === oldId;
            });

            if (prevSchedule && prevSchedule.modified) {
                if (prevSchedule.schedule_id !== -1) {
                    updateSchedule(prevSchedule);
                } else {
                    createSchedule(prevSchedule);
                }

                // Clear modified flag locally after saving (optimistic)
                setSchedules(prev => prev.map(s => {
                    const sId = s.schedule_id !== -1 ? s.schedule_id : `img-${s.imageinary_schedule_id}`;
                    if (sId === oldId) return { ...s, modified: false };
                    return s;
                }));
            }
        }
        prevEditingIdRef.current = newId;
    }, [editingScheduleId, schedules, updateSchedule, createSchedule, setSchedules]);

    const generateTimeSlots = useCallback(() => {
        const slots = [];
        for (let hour = startTime; hour <= endTime; hour++) {
            const displayHour = hour > 24 ? hour - 24 : hour;
            const nextHour = (hour + 1) > 24 ? hour - 23 : hour + 1;
            const timeLabel = t('timeFormat', {
                start: String(displayHour).padStart(2, '0'),
                end: String(nextHour).padStart(2, '0')
            });
            slots.push({ time: timeLabel, isFirstHalf: true, hour, minute: 0 });
            slots.push({ time: timeLabel, isFirstHalf: false, hour, minute: 30 });
        }
        return slots;
    }, [startTime, endTime, t]);

    const timeSlots = useMemo(() => generateTimeSlots(), [generateTimeSlots]);

    const addScheduleAt = useCallback(async (rowIndex, dayIndex, subjectInfo) => {
        if (!subjectInfo) return;

        const hours = Math.floor(startTime + (rowIndex / 2));
        const minutes = (rowIndex % 2) * 30;

        const currentDate = new Date(currentStartDay);
        currentDate.setDate(currentDate.getDate() + dayIndex);

        if (hours >= 24) {
            currentDate.setDate(currentDate.getDate() + 1);
        }

        const adjustedHours = hours >= 24 ? hours - 24 : hours;

        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');

        const timeString = `${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00.000`;
        const formattedDateTime = `${year}-${month}-${day}T${timeString}`;

        const newSchedule = {
            schedule_id: -1,
            imageinary_schedule_id: maxScheduleId,
            start_time: formattedDateTime,
            scheduled_time: subjectInfo.unit_time,
            subject_id: subjectInfo.subject_id,
            study_subject: {
                ...subjectInfo,
                category: { category_name: "Unspecified" }
            },
            modified: false
        };

        const id = `img-${maxScheduleId}`;
        setMaxScheduleId(maxScheduleId + 1);
        setSchedules(prev => [...prev, newSchedule]);
        setEditingScheduleId(id);

        // 즉시 서버 저장, 겹침 시 제거
        const result = await createSchedule(newSchedule);
        if (!result || !result.success) {
            setSchedules(prev => prev.filter(s => s.imageinary_schedule_id !== newSchedule.imageinary_schedule_id));
            setEditingScheduleId(null);
        }

    }, [maxScheduleId, currentStartDay, startTime, timeSlots, setMaxScheduleId, setSchedules, createSchedule]);

    const moveScheduleTo = useCallback(async (rowIndex, dayIndex, schedule) => {
        // Calculate new start time
        const hours = Math.floor(startTime + (rowIndex / 2));
        const minutes = (rowIndex % 2) * 30;

        const currentDate = new Date(currentStartDay);
        currentDate.setDate(currentDate.getDate() + dayIndex);

        if (hours >= 24) {
            currentDate.setDate(currentDate.getDate() + 1);
        }
        const adjustedHours = hours >= 24 ? hours - 24 : hours;

        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');

        const timeString = `${String(adjustedHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00.000`;
        const newStartTime = `${year}-${month}-${day}T${timeString}`;

        const id = schedule.schedule_id !== -1 ? schedule.schedule_id : schedule.imageinary_schedule_id;
        const isImaginary = schedule.schedule_id === -1;
        const oldStartTime = schedule.start_time;

        // 같은 위치면 무시
        if (new Date(oldStartTime).getTime() === new Date(newStartTime).getTime()) return;

        // Optimistic Update
        const updateFn = (s) => {
            if ((isImaginary && s.imageinary_schedule_id === id) || (!isImaginary && s.schedule_id === id)) {
                return { ...s, start_time: newStartTime };
            }
            return s;
        };
        setSchedules(prev => prev.map(updateFn));
        setEditingScheduleId(id);

        // 즉시 서버 저장
        const scheduleToSave = { ...schedule, start_time: newStartTime };
        const result = schedule.schedule_id !== -1
            ? await updateSchedule(scheduleToSave)
            : await createSchedule(scheduleToSave);

        // 겹침 또는 실패 시 원래 위치로 롤백
        if (!result || !result.success) {
            const rollbackFn = (s) => {
                if ((isImaginary && s.imageinary_schedule_id === id) || (!isImaginary && s.schedule_id === id)) {
                    return { ...s, start_time: oldStartTime };
                }
                return s;
            };
            setSchedules(prev => prev.map(rollbackFn));
        }
    }, [startTime, currentStartDay, setSchedules, updateSchedule, createSchedule]);

    // Mobile Drop Handler
    useEffect(() => {
        const handleGlobalTouchMove = (e) => {
            if (draggedSubjectInfo || draggedSchedule) {
                if (e.cancelable) e.preventDefault(); // Stop scrolling while dragging an item
                const touch = e.touches[0];
                setDragPosition({ x: touch.clientX, y: touch.clientY });
            }
        };

        const handleGlobalTouchEnd = (e) => {
            // ... existing logic
            if (!draggedSubjectInfo && !draggedSchedule) return;

            const touch = e.changedTouches[0];

            // Calculate Top-Left or use mouse pos
            let targetX = touch.clientX;
            let targetY = touch.clientY;

            if (draggedSchedule) {
                targetX = touch.clientX - dragOffset.x;
                targetY = touch.clientY - dragOffset.y;
            }

            // Margin for error? center of the top edge?
            // Let's use Top-Left + 5px inside to be safe
            const target = document.elementFromPoint(targetX + 5, targetY + 5);

            let cell = target;
            while (cell && cell.tagName !== 'TD' && cell.tagName !== 'BODY') {
                cell = cell.parentElement;
            }

            if (cell && cell.tagName === 'TD' && cell.classList.contains('timetable-cell')) {
                const dataKey = cell.getAttribute('data-key');
                if (dataKey) {
                    const [dIndex, rIndex] = dataKey.split('-').map(Number);

                    if (draggedSubjectInfo) {
                        addScheduleAt(rIndex, dIndex, draggedSubjectInfo);
                    } else if (draggedSchedule) {
                        moveScheduleTo(rIndex, dIndex, draggedSchedule);
                    }
                }
            }

            setDraggedSubjectInfo(null);
            setDraggedSchedule(null);
            setDragOffset({ x: 0, y: 0 });
        };

        if (draggedSubjectInfo || draggedSchedule) {
            // Need 'passive: false' to allow preventDefault
            window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
            window.addEventListener('touchend', handleGlobalTouchEnd);
        }

        return () => {
            window.removeEventListener('touchmove', handleGlobalTouchMove);
            window.removeEventListener('touchend', handleGlobalTouchEnd);
        };
    }, [draggedSubjectInfo, draggedSchedule, addScheduleAt, dragOffset.x, dragOffset.y, moveScheduleTo]);

    // Helper to clear time components for pure date comparison
    const clearTime = (date) => {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const formatToLocalISO = (date) => {
        const d = new Date(date);
        const pad = (n) => String(n).padStart(2, '0');
        const year = d.getFullYear();
        const month = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());
        const seconds = pad(d.getSeconds());
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000`;
    };

    // Calculate grid position for schedules
    const scheduleMap = useMemo(() => {
        const map = {}; // key: "dayIndex-rowIndex", value: [schedule]

        if (!schedules) return map;

        schedules.forEach(schedule => {
            // Skip todo schedules from the time grid (scheduled_time === 0이면 항상 TODO 취급)
            if (schedule.schedule_type === 'todo' || schedule.scheduled_time === 0) return;

            // 1. Calculate Day Index using Date objects (Handles Timezone correctly)
            // schedule.start_time is UTC (e.g., ...T10:00:00.000Z)
            // new Date() converts to Browser Local Time.
            // clearTime() sets Local Time to 00:00:00.

            const scheduleDateLocal = new Date(schedule.start_time);
            const scheduleDateCleared = clearTime(scheduleDateLocal); // Local midnight of schedule day
            const startDayCleared = clearTime(currentStartDay); // Local midnight of start day

            // Calculate difference in days based on Local Dates
            const diffTime = scheduleDateCleared.getTime() - startDayCleared.getTime();
            const dayIndex = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            // 2. Calculate Row Index using Local Hours from Date object
            const scheduleHour = scheduleDateLocal.getHours();
            const scheduleMinute = scheduleDateLocal.getMinutes();
            const startTimeInMinutes = startTime * 60;
            const endTimeInMinutes = endTime * 60;

            const addToMap = (di, hour) => {
                const mins = hour * 60 + scheduleMinute;
                if (mins < startTimeInMinutes || mins >= endTimeInMinutes) return;
                if (di < 0 || di >= 7) return;
                const row = Math.floor((mins - startTimeInMinutes) / 30);
                const key = `${di}-${row}`;
                if (!map[key]) map[key] = [];
                map[key].push({ ...schedule, startAdjust: 0 });
            };

            // 당일 표시 (0시 기준 그대로)
            addToMap(dayIndex, scheduleHour);

            // 전날 야간 표시 (자정~새벽을 전날 24+시간으로도 표시)
            if (scheduleHour < 24 - startTime && endTime > 24) {
                addToMap(dayIndex - 1, scheduleHour + 24);
            }
        });
        return map;
    }, [schedules, currentStartDay, startTime]);

    // 겹치는 스케줄의 레이아웃 계산 (너비 분할)
    const overlapLayout = useMemo(() => {
        const layout = {}; // key: schedule_id or imageinary_schedule_id → { index, count }
        if (!schedules) return layout;

        // 요일별로 겹침 그룹 계산
        const byDay = {};
        schedules.forEach(schedule => {
            if (schedule.schedule_type === 'todo' || schedule.scheduled_time === 0) return;
            const scheduleDateLocal = new Date(schedule.start_time);
            const startDayCleared = clearTime(currentStartDay);
            const diffTime = clearTime(scheduleDateLocal).getTime() - startDayCleared.getTime();
            const dayIndex = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            if (dayIndex < 0 || dayIndex >= 7) return;
            if (!byDay[dayIndex]) byDay[dayIndex] = [];
            const startMin = scheduleDateLocal.getHours() * 60 + scheduleDateLocal.getMinutes();
            const endMin = startMin + schedule.scheduled_time;
            const sid = schedule.schedule_id !== -1 ? schedule.schedule_id : `img-${schedule.imageinary_schedule_id}`;
            byDay[dayIndex].push({ sid, startMin, endMin });
        });

        // 각 요일에서 겹치는 그룹 찾기 (greedy column assignment)
        Object.values(byDay).forEach(daySchedules => {
            daySchedules.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);
            const columns = []; // 각 컬럼의 마지막 종료 시간
            const assigned = []; // { sid, col, startMin, endMin }

            daySchedules.forEach(s => {
                let placed = false;
                for (let c = 0; c < columns.length; c++) {
                    if (columns[c] <= s.startMin) {
                        columns[c] = s.endMin;
                        assigned.push({ ...s, col: c });
                        placed = true;
                        break;
                    }
                }
                if (!placed) {
                    assigned.push({ ...s, col: columns.length });
                    columns.push(s.endMin);
                }
            });

            // 연결된 겹침 그룹(connected component)별로 최대 컬럼 수 공유
            // Union-Find로 겹치는 스케줄을 하나의 그룹으로 묶기
            const parent = assigned.map((_, i) => i);
            const find = (i) => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
            const union = (a, b) => { parent[find(a)] = find(b); };

            for (let i = 0; i < assigned.length; i++) {
                for (let j = i + 1; j < assigned.length; j++) {
                    if (assigned[i].startMin < assigned[j].endMin && assigned[i].endMin > assigned[j].startMin) {
                        union(i, j);
                    }
                }
            }

            // 각 그룹의 최대 col+1 계산
            const groupMaxCols = {};
            assigned.forEach((s, i) => {
                const root = find(i);
                groupMaxCols[root] = Math.max(groupMaxCols[root] || 0, s.col + 1);
            });

            assigned.forEach((s, i) => {
                layout[s.sid] = { index: s.col, count: groupMaxCols[find(i)] };
            });
        });

        return layout;
    }, [schedules, currentStartDay]);

    // Group todo schedules by day index
    const todosByDay = useMemo(() => {
        const map = {};
        if (!schedules) return map;
        schedules.forEach(schedule => {
            const isTodo = schedule.schedule_type === 'todo' || schedule.scheduled_time === 0;
            if (!isTodo) return;
            const scheduleDateLocal = new Date(schedule.start_time);
            const scheduleDateCleared = clearTime(scheduleDateLocal);
            const startDayCleared = clearTime(currentStartDay);
            const diffTime = scheduleDateCleared.getTime() - startDayCleared.getTime();
            const dayIndex = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            if (dayIndex >= 0 && dayIndex < 7) {
                if (!map[dayIndex]) map[dayIndex] = [];
                map[dayIndex].push(schedule);
            }
        });
        return map;
    }, [schedules, currentStartDay]);

    const handleTodoAdd = async () => {
        if (isReadOnly || !todoSubjectId) return;
        let targetDate;
        if (todoDialog.dayDate) {
            targetDate = new Date(todoDialog.dayDate);
        } else if (todoDialog.dayIndex !== null) {
            targetDate = new Date(currentStartDay);
            targetDate.setDate(targetDate.getDate() + todoDialog.dayIndex);
        } else {
            return;
        }
        await createTodoSchedule(parseInt(todoSubjectId), targetDate, todoDescription || null);
        setTodoDialog({ isOpen: false, dayIndex: null, dayDate: null });
        setTodoSubjectId('');
        setTodoDescription('');
        if (viewMode === 'monthly') {
            fetchMonthlySchedules(currentMonth.year, currentMonth.month);
        } else {
            fetchScheduleByDate(currentStartDay, targetUserId);
        }
    };

    const handleTodoDoubleClick = (e, todo) => {
        handleTodoContextMenu(e, todo);
    };

    // Todo context menu handlers
    const handleTodoContextMenu = (e, todo) => {
        e.preventDefault();
        e.stopPropagation();
        if (isReadOnly) return;
        setTodoContextMenu({ x: e.clientX, y: e.clientY, schedule: todo });
    };

    const handleTodoEdit = () => {
        if (isReadOnly || !todoContextMenu) return;
        const schedule = todoContextMenu.schedule;
        const st = new Date(schedule.start_time);
        const pad = (n) => String(n).padStart(2, '0');
        const dateStr = `${st.getFullYear()}-${pad(st.getMonth() + 1)}-${pad(st.getDate())}`;
        setTodoEditDialog({
            schedule,
            specialText: schedule.special_text || '',
            dateStr,
            subjectId: schedule.subject_id
        });
        setTodoContextMenu(null);
    };

    const saveTodoEdit = () => {
        if (isReadOnly || !todoEditDialog) return;
        const { schedule, specialText, dateStr, subjectId } = todoEditDialog;
        // 날짜 변경: 기존 시간(시:분) 유지하면서 날짜만 변경
        const oldDate = new Date(schedule.start_time);
        const [y, m, d] = dateStr.split('-').map(Number);
        const newDate = new Date(y, m - 1, d, oldDate.getHours(), oldDate.getMinutes(), 0, 0);
        const newStartTime = formatLocalDateTime(newDate);
        const newSubject = subjects.find(s => s.subject_id === parseInt(subjectId));
        const updated = {
            ...schedule,
            special_text: specialText || null,
            start_time: newStartTime,
            subject_id: parseInt(subjectId),
            study_subject: newSubject ? { subject_id: newSubject.subject_id, subjectname: newSubject.subjectname, color: newSubject.color } : schedule.study_subject
        };
        updateSchedule(updated);
        const updateFn = (s) =>
            s.schedule_id === schedule.schedule_id ? updated : s;
        setSchedules(prev => prev.map(updateFn));
        setMonthlySchedules(prev => prev.map(updateFn));
        setTodoEditDialog(null);
    };

    const convertTodoToSchedule = () => {
        if (isReadOnly || !todoEditDialog) return;
        const { schedule, specialText, dateStr, subjectId } = todoEditDialog;
        const oldDate = new Date(schedule.start_time);
        const [y, m, d] = dateStr.split('-').map(Number);
        const newDate = new Date(y, m - 1, d, oldDate.getHours(), oldDate.getMinutes(), 0, 0);
        const newStartTime = formatLocalDateTime(newDate);
        const newSubject = subjects.find(s => s.subject_id === parseInt(subjectId));
        const scheduledTime = newSubject ? newSubject.unit_time : (schedule.study_subject?.unit_time || 60);
        const updated = {
            ...schedule,
            schedule_type: 'timed',
            scheduled_time: scheduledTime,
            completed: false,
            special_text: specialText || null,
            start_time: newStartTime,
            subject_id: parseInt(subjectId),
            study_subject: newSubject ? { subject_id: newSubject.subject_id, subjectname: newSubject.subjectname, color: newSubject.color } : schedule.study_subject
        };
        updateSchedule(updated);
        const updateFn = (s) =>
            s.schedule_id === schedule.schedule_id ? updated : s;
        setSchedules(prev => prev.map(updateFn));
        setMonthlySchedules(prev => prev.map(updateFn));
        setTodoEditDialog(null);
    };

    const handleTodoDeleteAction = () => {
        if (isReadOnly || !todoContextMenu) return;
        setTodoDeleteDialog({ isOpen: true, schedule: todoContextMenu.schedule });
        setTodoContextMenu(null);
    };

    const confirmTodoDelete = () => {
        const { schedule } = todoDeleteDialog;
        if (!schedule) return;
        deleteSchedule(schedule);
        setSchedules(prev => prev.filter(s => s.schedule_id !== schedule.schedule_id));
        setMonthlySchedules(prev => prev.filter(s => s.schedule_id !== schedule.schedule_id));
        setTodoDeleteDialog({ isOpen: false, schedule: null });
    };

    const handleTodoStartTimer = () => {
        if (!todoContextMenu) return;
        const todo = todoContextMenu.schedule;
        if (todo.completed) return;
        setTodoContextMenu(null);
        navigate('/timer', {
            state: {
                subject_id: todo.subject_id,
                schedule_id: todo.schedule_id,
                subject_name: todo.study_subject?.subjectname,
                special_text: todo.special_text || null,
                subject_color: todo.study_subject?.color,
                schedule_type: 'todo'
            }
        });
    };

    // Monthly data fetch
    const fetchMonthlySchedules = useCallback(async (year, month) => {
        if (!user) return;
        const startDate = new Date(year, month, 1);
        const endDate = new Date(year, month + 1, 1);
        const userIdParam = targetUserId ? `&userId=${targetUserId}` : '';
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/v1/time_table?startDate=${formatLocalDateTime(startDate)}&endDate=${formatLocalDateTime(endDate)}${userIdParam}`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } }
            );
            const data = await response.json();
            if (data.success) {
                setMonthlySchedules(data.schedules || []);
            }
        } catch (error) {
            console.error('Error fetching monthly schedules:', error);
        }
    }, [user, targetUserId]);

    useEffect(() => {
        if (viewMode === 'monthly') {
            fetchMonthlySchedules(currentMonth.year, currentMonth.month);
        }
    }, [viewMode, currentMonth, fetchMonthlySchedules]);

    // Monthly TODO toggle handler (updates monthlySchedules optimistically)
    const handleMonthlyTodoToggle = (scheduleId, completed) => {
        if (isReadOnly) return;
        toggleTodoCompleted(scheduleId, completed);
        setMonthlySchedules(prev =>
            prev.map(s => s.schedule_id === scheduleId ? { ...s, completed } : s)
        );
    };

    // Monthly schedule context menu (timed schedules)
    const handleMonthlyScheduleContextMenu = (e, schedule) => {
        e.preventDefault();
        e.stopPropagation();
        if (isReadOnly) return;
        setContextMenu({ x: e.clientX, y: e.clientY, schedule });
    };

    // Weekly TODO touch handlers
    const handleWeeklyTodoTouchStart = useCallback((e, todo) => {
        const touch = e.touches[0];
        weeklyTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
        weeklyTouchDragRef.current = null;

        weeklyTouchTimerRef.current = setTimeout(() => {
            weeklyTouchTimerRef.current = null;
            if (!targetUserId) {
                // Start touch drag
                weeklyTouchDragRef.current = todo;
                weeklyTouchDragElRef.current = e.target.closest('.todo-row-item');
                if (weeklyTouchDragElRef.current) weeklyTouchDragElRef.current.style.opacity = '0.5';
                setWeeklyTouchDragging(true);
            } else {
                // Observer mode: open context menu
                setTodoContextMenu({ x: touch.clientX, y: touch.clientY, schedule: todo });
            }
        }, 400);
    }, [targetUserId]);

    const handleWeeklyTodoTouchMove = useCallback((e) => {
        const touch = e.touches[0];
        if (weeklyTouchTimerRef.current && weeklyTouchStartRef.current) {
            const dist = Math.sqrt(
                Math.pow(touch.clientX - weeklyTouchStartRef.current.x, 2) +
                Math.pow(touch.clientY - weeklyTouchStartRef.current.y, 2)
            );
            if (dist > 10) {
                clearTimeout(weeklyTouchTimerRef.current);
                weeklyTouchTimerRef.current = null;
            }
        }
        if (weeklyTouchDragRef.current) {
            if (e.cancelable) e.preventDefault();
            const el = document.elementFromPoint(touch.clientX, touch.clientY);
            const cell = el?.closest('.todo-row-cell');
            if (cell) {
                const idx = parseInt(cell.getAttribute('data-dayindex'), 10);
                if (!isNaN(idx)) setWeeklyDragOverDay(idx);
            } else {
                setWeeklyDragOverDay(null);
            }
        }
    }, []);

    const handleWeeklyTodoTouchEnd = useCallback(() => {
        if (weeklyTouchTimerRef.current) {
            clearTimeout(weeklyTouchTimerRef.current);
            weeklyTouchTimerRef.current = null;
        }
        if (weeklyTouchDragRef.current && weeklyDragOverDay !== null) {
            const todo = weeklyTouchDragRef.current;
            const cellDate = new Date(currentStartDay);
            cellDate.setDate(cellDate.getDate() + weeklyDragOverDay);
            const oldDate = new Date(todo.start_time);
            const newDate = new Date(cellDate);
            newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
            const newStartTime = formatLocalDateTime(newDate);
            const updated = { ...todo, start_time: newStartTime };
            updateSchedule(updated);
            setMonthlySchedules(prev =>
                prev.map(s => s.schedule_id === todo.schedule_id ? updated : s)
            );
            setSchedules(prev =>
                prev.map(s => s.schedule_id === todo.schedule_id ? updated : s)
            );
        }
        if (weeklyTouchDragElRef.current) {
            weeklyTouchDragElRef.current.style.opacity = '1';
            weeklyTouchDragElRef.current = null;
        }
        weeklyTouchDragRef.current = null;
        weeklyTouchStartRef.current = null;
        setWeeklyTouchDragging(false);
        setWeeklyDragOverDay(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weeklyDragOverDay, currentStartDay, updateSchedule, setMonthlySchedules, setSchedules]);

    // Global touch listeners for weekly drag
    useEffect(() => {
        if (!weeklyTouchDragging) return;
        const onMove = (e) => handleWeeklyTodoTouchMove(e);
        const onEnd = () => handleWeeklyTodoTouchEnd();
        window.addEventListener('touchmove', onMove, { passive: false });
        window.addEventListener('touchend', onEnd);
        return () => {
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onEnd);
        };
    }, [weeklyTouchDragging, handleWeeklyTodoTouchMove, handleWeeklyTodoTouchEnd]);

    // Cleanup weekly touch timer on unmount
    useEffect(() => {
        return () => {
            if (weeklyTouchTimerRef.current) clearTimeout(weeklyTouchTimerRef.current);
        };
    }, []);

    // Monthly TODO drag & drop
    const handleTodoDrop = (todo, targetDate) => {
        if (isReadOnly) return;
        const oldDate = new Date(todo.start_time);
        const newDate = new Date(targetDate);
        newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
        const newStartTime = formatLocalDateTime(newDate);
        const updated = { ...todo, start_time: newStartTime };
        updateSchedule(updated);
        setMonthlySchedules(prev =>
            prev.map(s => s.schedule_id === todo.schedule_id ? updated : s)
        );
        setSchedules(prev =>
            prev.map(s => s.schedule_id === todo.schedule_id ? updated : s)
        );
    };

    const handleStartTimeChange = (e) => {
        const value = parseInt(e.target.value);
        if (value >= 0 && value <= 48) {
            setStartTime(value);
            localStorage.setItem('startTime', value);
        }
    };

    const handleEndTimeChange = (e) => {
        const value = parseInt(e.target.value);
        if (value >= 0 && value <= 48) {
            setEndTime(value);
            localStorage.setItem('endTime', value);
        }
    };

    const handleScheduleDragStart = (e, schedule) => {
        if (isReadOnly) return;
        // e.dataTransfer.setData("text/plain", JSON.stringify(schedule)); // Optional if we use state

        // Hide default drag image
        const emptyImage = new Image();
        emptyImage.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        e.dataTransfer.setDragImage(emptyImage, 0, 0);

        setDraggedSchedule(schedule);
        setDraggedSubjectInfo(null); // Ensure we are not creating new
        setDragOffset({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
        setDragPosition({ x: e.clientX, y: e.clientY }); // Initialize drag position

        // Capture dimensions
        const rect = e.currentTarget.getBoundingClientRect();
        setDragDimensions({ width: rect.width, height: rect.height });

        // Auto-select on drag start
        const id = schedule.schedule_id !== -1 ? schedule.schedule_id : `img-${schedule.imageinary_schedule_id}`;
        setEditingScheduleId(id);
    };

    const handleDragEnd = useCallback((e, rowIndex, dayIndex) => {
        if (isReadOnly) return;
        if (e && e.preventDefault) e.preventDefault();

        if (draggedSubjectInfo) {
            // Case 1: Creating New Schedule
            addScheduleAt(rowIndex, dayIndex, draggedSubjectInfo);
            setDraggedSubjectInfo(null);
        } else if (draggedSchedule) {
            // Case 2: Moving Existing Schedule - Use Top-Left
            const clientX = e.type.startsWith('touch') ? e.changedTouches[0].clientX : e.clientX;
            const clientY = e.type.startsWith('touch') ? e.changedTouches[0].clientY : e.clientY;

            const targetX = clientX - dragOffset.x;
            const targetY = clientY - dragOffset.y;

            // Find cell at Top-Left
            const targetElement = document.elementFromPoint(targetX + 5, targetY + 5);
            let cell = targetElement;
            while (cell && cell.tagName !== 'TD' && cell.tagName !== 'BODY') {
                cell = cell.parentElement;
            }

            if (cell && cell.tagName === 'TD' && cell.classList.contains('timetable-cell')) {
                const dataKey = cell.getAttribute('data-key');
                if (dataKey) {
                    const [dIndex, rIndex] = dataKey.split('-').map(Number);
                    moveScheduleTo(rIndex, dIndex, draggedSchedule);
                }
            }

            setDraggedSchedule(null);
            setDragOffset({ x: 0, y: 0 });
            setDragPosition({ x: 0, y: 0 });
            setDragDimensions(null);
        }
    }, [isReadOnly, draggedSubjectInfo, draggedSchedule, dragOffset, addScheduleAt, moveScheduleTo]);
    const handleScheduleDragEnd = () => {
        setDraggedSchedule(null);
        setDragDimensions(null);
        setDragOffset({ x: 0, y: 0 });
        setDragPosition({ x: 0, y: 0 });
    };

    // Resize Handlers
    const handleResizeStart = (e, schedule, direction) => {
        if (isReadOnly) return;
        e.stopPropagation(); // Prevent parent drag start for both touch and mouse
        if (e.type === 'mousedown') {
            e.preventDefault(); // Prevent text selection
        }

        const clientY = e.type.startsWith('touch') ? (e.touches[0] ? e.touches[0].clientY : 0) : e.clientY;

        if (e.type === 'touchstart' && e.cancelable) {
            e.preventDefault(); // Immediate prevent scroll
        }

        setResizingSchedule({
            schedule,
            direction,
            initialY: clientY,
            initialTime: new Date(schedule.start_time).getTime(),
            initialDuration: schedule.scheduled_time
        });
    };

    useEffect(() => {
        const handleGlobalMouseMove = (e) => {
            if (!resizingSchedule) return;

            // Prevent scrolling on touch devices during resize
            if (e.type.startsWith('touch') && e.cancelable) {
                e.preventDefault();
            }

            const { schedule, direction, initialY, initialTime, initialDuration } = resizingSchedule;
            const clientY = e.type.startsWith('touch')
                ? (e.touches && e.touches[0] ? e.touches[0].clientY : (e.changedTouches ? e.changedTouches[0].clientY : initialY))
                : e.clientY;

            if (clientY === undefined || isNaN(clientY)) return;
            const deltaY = clientY - initialY;

            // cellHeight pixels = 30 minutes
            const PIXELS_PER_MINUTE = cellHeight / 30;
            const deltaMinutes = deltaY / PIXELS_PER_MINUTE;

            const SNAP_MINUTES = 30;
            const snappedDelta = Math.round(deltaMinutes / SNAP_MINUTES) * SNAP_MINUTES;

            if (snappedDelta === 0) return;

            // Check collision with other schedules on the same day
            const currentScheduleDateCleared = clearTime(new Date(initialTime));
            const daySchedules = schedulesRef.current.filter(s => {
                const sDateCleared = clearTime(new Date(s.start_time));
                const isSameDay = sDateCleared.getTime() === currentScheduleDateCleared.getTime();
                const isOther = (s.schedule_id !== -1 && s.schedule_id !== schedule.schedule_id) ||
                    (s.schedule_id === -1 && s.imageinary_schedule_id !== schedule.imageinary_schedule_id);
                return isSameDay && isOther;
            });

            let newStartTime = new Date(initialTime);
            let newDuration = initialDuration;

            if (direction === 'top') {
                newStartTime = new Date(initialTime + (snappedDelta * 60 * 1000));
                newDuration = initialDuration - snappedDelta;
            } else {
                newDuration = initialDuration + snappedDelta;
            }

            if (newDuration < 10 || isNaN(newStartTime.getTime())) return;

            const checkStart = newStartTime;
            const checkEnd = new Date(newStartTime.getTime() + (newDuration * 60 * 1000));

            let hasCollision = false;
            for (const other of daySchedules) {
                const otherStart = new Date(other.start_time);
                const otherEnd = new Date(otherStart.getTime() + (other.scheduled_time * 60 * 1000));

                if (checkStart < otherEnd && checkEnd > otherStart) {
                    hasCollision = true;
                    break;
                }
            }

            if (hasCollision) return;

            const resizeUpdateFn = (s) => {
                if ((s.schedule_id === schedule.schedule_id && s.schedule_id !== -1) ||
                    (s.schedule_id === -1 && s.imageinary_schedule_id === schedule.imageinary_schedule_id)) {
                    return {
                        ...s,
                        start_time: formatToLocalISO(newStartTime),
                        scheduled_time: newDuration,
                        modified: true
                    };
                }
                return s;
            };
            setSchedules(prev => prev.map(resizeUpdateFn));
        };

        const handleGlobalMouseUp = () => {
            if (resizingSchedule) {
                const { schedule } = resizingSchedule;

                // Auto-select the resized schedule
                const id = schedule.schedule_id !== -1 ? schedule.schedule_id : `img-${schedule.imageinary_schedule_id}`;
                setEditingScheduleId(id);

                setResizingSchedule(null);
            }
        };

        if (resizingSchedule) {
            window.addEventListener('mousemove', handleGlobalMouseMove);
            window.addEventListener('mouseup', handleGlobalMouseUp);
            window.addEventListener('touchmove', handleGlobalMouseMove, { passive: false });
            window.addEventListener('touchend', handleGlobalMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleGlobalMouseMove);
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('touchmove', handleGlobalMouseMove);
            window.removeEventListener('touchend', handleGlobalMouseUp);
        };
    }, [resizingSchedule, cellHeight, setSchedules]);

    useEffect(() => {
        const handleGlobalTouchMove = (e) => {
            if (draggedSchedule || draggedSubjectInfo) {
                const touch = e.touches[0];
                setDragPosition({ x: touch.clientX, y: touch.clientY });
                // 실제 이동 발생 → 탭이 아닌 드래그로 판정
                scheduleTouchRef.current.moved = true;
                // 드래그 중 스크롤 방지
                if (e.cancelable) e.preventDefault();
            }
        };

        const handleGlobalTouchEnd = (e) => {
            if (draggedSchedule || draggedSubjectInfo) {
                // 탭(이동 없음)이면 드래그 처리 건너뛰고 정리만 수행
                if (!scheduleTouchRef.current.moved) {
                    setDraggedSchedule(null);
                    setDragPosition(null);
                    return;
                }
                handleDragEnd(e);
            }
        };

        if (draggedSchedule || draggedSubjectInfo) {
            window.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
            window.addEventListener('touchend', handleGlobalTouchEnd);
        }

        return () => {
            window.removeEventListener('touchmove', handleGlobalTouchMove);
            window.removeEventListener('touchend', handleGlobalTouchEnd);
        };
    }, [draggedSchedule, draggedSubjectInfo, dragOffset, handleDragEnd]);



    const handleContextMenu = (e, schedule) => {
        e.preventDefault();
        e.stopPropagation();
        if (isReadOnly) return;
        setContextMenu({
            x: e.clientX,
            y: e.clientY,
            schedule
        });
    };

    const handleDeleteSchedule = () => {
        if (isReadOnly || !contextMenu) return;
        const { schedule } = contextMenu;
        setDeleteDialog({ isOpen: true, schedule });
        setContextMenu(null);
    };

    const confirmDeleteSchedule = () => {
        const { schedule } = deleteDialog;
        if (!schedule) return;

        const id = schedule.schedule_id !== -1 ? schedule.schedule_id : schedule.imageinary_schedule_id;
        const isImaginary = schedule.schedule_id === -1;

        if (isImaginary) {
            setSchedules(prev => prev.filter(s => s.imageinary_schedule_id !== id));
        } else {
            setRemovedSchedules(prev => [...prev, schedule]);
            setSchedules(prev => prev.filter(s => s.schedule_id !== id));
            setMonthlySchedules(prev => prev.filter(s => s.schedule_id !== id));
            deleteSchedule(schedule);
        }

        // Clear selection if the deleted schedule was selected
        const sId = schedule.schedule_id !== -1 ? schedule.schedule_id : `img-${schedule.imageinary_schedule_id}`;
        if (editingScheduleId === sId) {
            setEditingScheduleId(null);
        }

        setDeleteDialog({ isOpen: false, schedule: null });
    };

    const handleStartTimer = () => {
        if (!contextMenu) return;
        const { schedule } = contextMenu;
        if (schedule.completed) return;
        setContextMenu(null);
        navigate('/timer', {
            state: {
                schedule_id: schedule.schedule_id,
                subject_id: schedule.subject_id,
                subject_name: schedule.study_subject?.subjectname || '',
                special_text: schedule.special_text || null,
                subject_color: schedule.study_subject?.color || '#ccc',
                start_time: schedule.start_time,
                scheduled_time: schedule.scheduled_time
            }
        });
    };

    const handleEditSchedule = () => {
        if (isReadOnly || !contextMenu) return;
        const { schedule } = contextMenu;
        // start_time을 datetime-local 형식으로 변환
        const st = new Date(schedule.start_time);
        const pad = (n) => String(n).padStart(2, '0');
        const startTimeLocal = `${st.getFullYear()}-${pad(st.getMonth() + 1)}-${pad(st.getDate())}T${pad(st.getHours())}:${pad(st.getMinutes())}`;
        setEditDialog({
            schedule,
            time: schedule.scheduled_time,
            startTimeLocal,
            subjectId: schedule.subject_id,
            specialText: schedule.special_text || ''
        });
        setContextMenu(null);
    };

    const handleDetachSchedule = async () => {
        if (isReadOnly || !contextMenu) return;
        const { schedule } = contextMenu;
        setContextMenu(null);

        try {
            const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/recurring-rules/detach/${schedule.schedule_id}`, {
                method: 'PUT',
            });
            const data = await res.json();
            if (data.success) {
                setSchedules(prev => prev.map(s =>
                    s.schedule_id === schedule.schedule_id ? { ...s, recurring_rule_id: null } : s
                ));
            }
        } catch (e) {
            console.error('Failed to detach schedule:', e);
        }
    };

    const handleTodoDetach = async () => {
        if (!todoContextMenu) return;
        const todo = todoContextMenu.schedule;
        setTodoContextMenu(null);

        try {
            const res = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/recurring-rules/detach/${todo.schedule_id}`, {
                method: 'PUT',
            });
            const data = await res.json();
            if (data.success) {
                setSchedules(prev => prev.map(s =>
                    s.schedule_id === todo.schedule_id ? { ...s, recurring_rule_id: null } : s
                ));
            }
        } catch (e) {
            console.error('Failed to detach todo:', e);
        }
    };

    const saveEdit = () => {
        if (isReadOnly || !editDialog) return;
        const { schedule, time, startTimeLocal, subjectId, specialText } = editDialog;

        const id = schedule.schedule_id !== -1 ? schedule.schedule_id : schedule.imageinary_schedule_id;
        const isImaginary = schedule.schedule_id === -1;

        // datetime-local → API 형식
        const newStartTime = formatLocalDateTime(new Date(startTimeLocal));
        const newSubject = subjects.find(s => s.subject_id === parseInt(subjectId));

        let updatedScheduleForApi = null;

        const updateFn = (s) => {
            if ((isImaginary && s.imageinary_schedule_id === id) || (!isImaginary && s.schedule_id === id)) {

                const isChanged =
                    s.scheduled_time !== parseInt(time) ||
                    new Date(s.start_time).getTime() !== new Date(newStartTime).getTime() ||
                    s.special_text !== specialText ||
                    s.subject_id !== parseInt(subjectId);

                const updated = {
                    ...s,
                    scheduled_time: parseInt(time),
                    start_time: newStartTime,
                    subject_id: parseInt(subjectId),
                    special_text: specialText.trim() || null,
                    modified: s.modified || isChanged,
                    study_subject: newSubject ? { subject_id: newSubject.subject_id, subjectname: newSubject.subjectname, color: newSubject.color } : s.study_subject
                };
                updatedScheduleForApi = updated;
                return updated;
            }
            return s;
        };

        setSchedules(prev => prev.map(updateFn));
        setMonthlySchedules(prev => prev.map(updateFn));
        setEditDialog(null);

        // Immediately save to API instead of deferring
        if (updatedScheduleForApi && updatedScheduleForApi.modified) {
            if (updatedScheduleForApi.schedule_id !== -1) {
                updateSchedule(updatedScheduleForApi);
            } else {
                createSchedule(updatedScheduleForApi);
            }
        }
        setEditingScheduleId(null);
    };

    const convertScheduleToTodo = () => {
        if (isReadOnly || !editDialog) return;
        const { schedule, specialText } = editDialog;
        const updated = { ...schedule, schedule_type: 'todo', scheduled_time: 0, completed: false, special_text: specialText || null };
        updateSchedule(updated);
        const updateFn = (s) =>
            s.schedule_id === schedule.schedule_id ? { ...s, schedule_type: 'todo', scheduled_time: 0, completed: false, special_text: specialText || null } : s;
        setSchedules(prev => prev.map(updateFn));
        setMonthlySchedules(prev => prev.map(updateFn));
        setEditDialog(null);
    };

    const handleCopyLastWeek = () => {
        // Trigger generic fetch which populates lastWeekSchedules
        fetchLastWeekSchedules(targetUserId);
    };

    // PDF Export - capture and show preview
    const handleExportPdf = async () => {
        if (!printRef.current || isPdfExporting) return;
        setIsPdfExporting(true);
        try {
            const element = printRef.current;
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
                logging: false,
            });

            const imgData = canvas.toDataURL('image/png');
            const imgWidth = canvas.width;
            const imgHeight = canvas.height;

            const isLandscape = viewMode === 'weekly';
            const pdf = new jsPDF({
                orientation: isLandscape ? 'landscape' : 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const availWidth = pageWidth - margin * 2;
            const availHeight = pageHeight - margin * 2;

            const ratio = Math.min(availWidth / imgWidth, availHeight / imgHeight);
            const pdfImgWidth = imgWidth * ratio;
            const pdfImgHeight = imgHeight * ratio;

            const x = (pageWidth - pdfImgWidth) / 2;
            const y = margin;

            pdf.addImage(imgData, 'PNG', x, y, pdfImgWidth, pdfImgHeight);

            let fileName;
            if (viewMode === 'weekly') {
                const weekNum = String(Math.ceil((currentStartDay.getTime() - new Date(currentStartDay.getFullYear(), 0, 1).getTime()) / (7 * 86400000))).padStart(2, '0');
                fileName = `timetable_${currentStartDay.getFullYear()}_W${weekNum}.pdf`;
            } else {
                fileName = `timetable_${currentMonth.year}_${String(currentMonth.month + 1).padStart(2, '0')}.pdf`;
            }

            // Show preview instead of direct save
            setPdfPreview({ imgDataUrl: imgData, pdfInstance: pdf, fileName });
        } catch (error) {
            console.error('PDF export failed:', error);
        } finally {
            setIsPdfExporting(false);
        }
    };

    const handleShareImage = async () => {
        if (!printRef.current) return;
        try {
            const canvas = await html2canvas(printRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
                logging: false,
            });
            const imageDataUrl = canvas.toDataURL('image/png');
            const label = viewMode === 'weekly'
                ? `${t('view.weekly')} - ${currentStartDay.toLocaleDateString()}`
                : `${t('view.monthly')} - ${currentMonth.year}.${currentMonth.month + 1}`;
            setShareData({
                imageDataUrl,
                label,
                date: new Date().toLocaleDateString(),
                username: user?.username || '',
            });
            setShareDialogOpen(true);
        } catch (error) {
            console.error('Share capture failed:', error);
        }
    };

    const [isPngExporting, setIsPngExporting] = useState(false);
    const [pngPreview, setPngPreview] = useState(null); // { imgDataUrl, blob, fileName }

    const handleExportPng = async () => {
        if (!printRef.current || isPngExporting) return;
        setIsPngExporting(true);
        try {
            const canvas = await html2canvas(printRef.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--card-bg').trim() || '#ffffff',
                logging: false,
            });
            const blob = await canvasToBlob(canvas);
            const imgDataUrl = canvas.toDataURL('image/png');
            let fileName;
            if (viewMode === 'weekly') {
                const weekNum = String(Math.ceil((currentStartDay.getTime() - new Date(currentStartDay.getFullYear(), 0, 1).getTime()) / (7 * 86400000))).padStart(2, '0');
                fileName = `timetable_${currentStartDay.getFullYear()}_W${weekNum}.png`;
            } else {
                fileName = `timetable_${currentMonth.year}_${String(currentMonth.month + 1).padStart(2, '0')}.png`;
            }
            setPngPreview({ imgDataUrl, blob, fileName });
        } catch (error) {
            console.error('PNG export failed:', error);
        } finally {
            setIsPngExporting(false);
        }
    };

    const handlePngSave = () => {
        if (pngPreview) {
            downloadImage(pngPreview.blob, pngPreview.fileName);
            setPngPreview(null);
        }
    };

    const handlePdfSave = () => {
        if (pdfPreview) {
            pdfPreview.pdfInstance.save(pdfPreview.fileName);
            setPdfPreview(null);
        }
    };

    // Effect for handling last week copy logic
    useEffect(() => {
        if (lastWeekSchedules.length > 0) {
            const apiSchedules = lastWeekSchedules
                .filter(schedule => schedule.dimmed !== 1 && !schedule.recurring_rule_id)
                .map(schedule => {
                    const scheduleDate = new Date(schedule.start_time);
                    scheduleDate.setDate(scheduleDate.getDate() + 7);
                    return {
                        subject_id: schedule.subject_id,
                        scheduled_time: schedule.scheduled_time,
                        start_time: formatToLocalISO(scheduleDate),
                        schedule_type: schedule.schedule_type || 'timed',
                        completed: false,
                        dimmed: false
                    };
                });

            setLastWeekSchedules([]); // 즉시 클리어하여 재실행 방지

            if (apiSchedules.length > 0) {
                (async () => {
                    await createSchedule(apiSchedules, { skipOverlapCheck: true });
                })();
            }
        }
    }, [lastWeekSchedules, createSchedule, setLastWeekSchedules]);


    // Render Helpers
    const renderHeaders = () => {
        const headers = [<th key="time" className="time-header-cell">{t('time')}</th>];
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];




        // Logic adjusted to standard 0-6 iteration based on currentStartDay
        for (let i = 0; i < 7; i++) {
            const date = new Date(currentStartDay);
            date.setDate(date.getDate() + i);
            const dayName = t(days[date.getDay()]); // date.getDay() returns 0 for Sun

            let color = 'black';
            if (date.getDay() === 0) color = 'red';
            if (date.getDay() === 6) color = 'blue';

            headers.push(
                <th key={i} style={{ color }}>
                    {date.getMonth() + 1}/{date.getDate()}<br />{dayName}
                </th>
            );
        }
        return headers;
    };

    return (
        <div className="timetable-container" onClick={() => setEditingScheduleId(null)}>

            <div className="time-settings">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <label>{t('startTime')}: </label>
                    <input type="number" min="0" max="23" value={startTime} onChange={handleStartTimeChange} />

                    <label>{t('endTime')}: </label>
                    <input type="number" min="0" max="28" value={endTime} onChange={handleEndTimeChange} />

                    <label>{t('startWithMonday')}: </label>
                    <input type="checkbox" checked={startWithMonday} onChange={(e) => {
                        setStartWithMonday(e.target.checked);
                        localStorage.setItem('startWithMonday', e.target.checked);
                        fetchScheduleByDate(currentStartDay, targetUserId);
                    }} />

                    {!isReadOnly && (
                        <button onClick={() => setIsSubjectPaletteOpen(!isSubjectPaletteOpen)} className="edit-button">
                            {t('update')}
                        </button>
                    )}

                    {/* Student Selector */}
                    {myStudents.length > 0 && (
                        <select
                            value={selectedStudentId}
                            onChange={handleStudentChange}
                            style={{ padding: '5px', borderRadius: '4px', border: '1px solid #ccc' }}
                        >
                            <option value="">{t('My TimeTable')}</option>
                            {myStudents.filter(s => s.perm_timetable).map(student => (
                                <option key={student.user_id} value={student.user_id}>
                                    {student.username}
                                </option>
                            ))}
                        </select>
                    )}

                    {!isReadOnly && (
                        <button onClick={handleCopyLastWeek} className="edit-button" style={{ backgroundColor: '#2196F3' }}>
                            {t('copyLastWeek')}
                        </button>
                    )}
                </div>
            </div>

            {/* View Toggle */}
            <div className="view-toggle-container">
                <div className="view-toggle">
                    <button
                        className="pdf-export-btn"
                        onClick={handleExportPdf}
                        disabled={isPdfExporting}
                        title={t('pdf.export')}
                    >
                        {isPdfExporting ? t('pdf.exporting') : t('pdf.export')}
                    </button>
                    <button
                        className="png-export-btn"
                        onClick={handleExportPng}
                        disabled={isPngExporting}
                        title={t('png.export')}
                    >
                        {isPngExporting ? t('png.exporting') : t('png.export')}
                    </button>
                    <button
                        className="share-export-btn"
                        onClick={handleShareImage}
                        title={t('share.share')}
                    >
                        {t('share.share')}
                    </button>
                    <button
                        className={`view-toggle-btn ${viewMode === 'weekly' ? 'active' : ''}`}
                        onClick={() => {
                            // 월간 → 주간: 현재 주가 해당 월에 속하면 유지, 아니면 재계산
                            const weekMid = new Date(currentStartDay);
                            weekMid.setDate(weekMid.getDate() + 3);
                            const alreadyInMonth = weekMid.getFullYear() === currentMonth.year && weekMid.getMonth() === currentMonth.month;
                            if (!alreadyInMonth) {
                                const now = new Date();
                                const baseDate = (now.getFullYear() === currentMonth.year && now.getMonth() === currentMonth.month)
                                    ? now : new Date(currentMonth.year, currentMonth.month, 1);
                                const dayOfWeek = baseDate.getDay();
                                const offset = startWithMonday ? (dayOfWeek === 0 ? -6 : 1 - dayOfWeek) : -dayOfWeek;
                                const weekStart = new Date(baseDate);
                                weekStart.setDate(weekStart.getDate() + offset);
                                weekStart.setHours(0, 0, 0, 0);
                                setCurrentStartDay(weekStart);
                            }
                            setViewMode('weekly');
                            localStorage.setItem('viewMode', 'weekly');
                        }}
                    >
                        {t('view.weekly')}
                    </button>
                    <button
                        className={`view-toggle-btn ${viewMode === 'monthly' ? 'active' : ''}`}
                        onClick={() => {
                            // 주간 → 월간: 미저장 스케줄 먼저 flush
                            flushPendingSchedules();
                            const mid = new Date(currentStartDay);
                            mid.setDate(mid.getDate() + 3);
                            setCurrentMonth({ year: mid.getFullYear(), month: mid.getMonth() });
                            setViewMode('monthly');
                            localStorage.setItem('viewMode', 'monthly');
                        }}
                    >
                        {t('view.monthly')}
                    </button>
                </div>
            </div>

            <div ref={printRef}>
            {/* Date Navigation */}
            {viewMode === 'weekly' ? (<>
                <div className="date-nav">
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                        <button className="date-nav-btn" onClick={() => {
                            const newDate = new Date(currentStartDay);
                            newDate.setDate(newDate.getDate() - 7);
                            setCurrentStartDay(newDate);
                            fetchScheduleByDate(newDate, targetUserId);
                        }}>{'\u25C0'}</button>

                        <span>
                            {currentStartDay.getFullYear()} W{String(Math.ceil((currentStartDay.getTime() - new Date(currentStartDay.getFullYear(), 0, 1).getTime()) / (7 * 86400000))).padStart(2, '0')}
                        </span>

                        <button className="date-nav-btn" onClick={() => {
                            const newDate = new Date(currentStartDay);
                            newDate.setDate(newDate.getDate() + 7);
                            setCurrentStartDay(newDate);
                            fetchScheduleByDate(newDate, targetUserId);
                        }}>{'\u25B6'}</button>
                    </div>
                    <button
                        className="date-nav-action-btn"
                        onClick={() => { setCurrentStartDaywithToday(); fetchScheduleByDate(currentStartDay, targetUserId); }}
                    >
                        {t('thisWeek')}
                    </button>
                    <button
                        onClick={() => setShowActualOverlay(prev => !prev)}
                        style={{
                            fontSize: '14px', padding: '3px 8px', borderRadius: '4px', border: 'none', marginTop: '5px', marginLeft: '8px',
                            backgroundColor: showActualOverlay ? '#4caf50' : 'var(--btn-secondary-bg)',
                            color: showActualOverlay ? 'white' : 'var(--text-primary)',
                            cursor: 'pointer'
                        }}
                    >
                        {t('timer.showActual')}
                    </button>
                </div>
                {showActualOverlay && (
                    <div className="actual-legend">
                        <span className="actual-legend-planned"></span> {t('timer.planned')}
                        <span className="actual-legend-actual"></span> {t('timer.actual')}
                    </div>
                )}
            </>) : (
                <div className="date-nav">
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
                        <button className="date-nav-btn" onClick={() => {
                            setCurrentMonth(prev => {
                                const newMonth = prev.month - 1;
                                if (newMonth < 0) return { year: prev.year - 1, month: 11 };
                                return { ...prev, month: newMonth };
                            });
                        }}>{'\u25C0'}</button>

                        <span>
                            {t('monthNames.format', { year: currentMonth.year, month: currentMonth.month + 1 })}
                        </span>

                        <button className="date-nav-btn" onClick={() => {
                            setCurrentMonth(prev => {
                                const newMonth = prev.month + 1;
                                if (newMonth > 11) return { year: prev.year + 1, month: 0 };
                                return { ...prev, month: newMonth };
                            });
                        }}>{'\u25B6'}</button>
                    </div>
                    <button
                        onClick={() => {
                            const now = new Date();
                            setCurrentMonth({ year: now.getFullYear(), month: now.getMonth() });
                        }}
                        className="date-nav-action-btn"
                    >
                        {t('thisMonth')}
                    </button>
                </div>
            )}

            {/* Monthly Calendar View */}
            {viewMode === 'monthly' && (
                <MonthlyCalendar
                    year={currentMonth.year}
                    month={currentMonth.month}
                    schedules={monthlySchedules}
                    subjects={subjects}
                    onTodoAdd={isReadOnly ? null : (date) => {
                        setTodoDialog({ isOpen: true, dayIndex: null, dayDate: date });
                        setTodoSubjectId('');
                        setTodoDescription('');
                    }}
                    onTodoToggle={handleMonthlyTodoToggle}
                    onTodoContextMenu={handleTodoContextMenu}
                    onTodoDoubleClick={handleTodoDoubleClick}
                    onScheduleContextMenu={handleMonthlyScheduleContextMenu}
                    onScheduleDoubleClick={handleMonthlyScheduleContextMenu}
                    onTodoDrop={handleTodoDrop}
                    targetUserId={targetUserId}
                    startWithMonday={startWithMonday}
                    t={t}
                />
            )}

            {/* The Table (Weekly View) */}
            {viewMode === 'weekly' && <div className="timetable">
                <table>
                    <thead>
                        <tr>{renderHeaders()}</tr>
                    </thead>
                    <tbody>
                        {/* ToDo Row */}
                        <tr className="todo-row">
                            <td className="todo-row-label">{t('todo.title')}</td>
                            {Array.from({ length: 7 }).map((_, dayIndex) => {
                                const cellDate = new Date(currentStartDay);
                                cellDate.setDate(cellDate.getDate() + dayIndex);
                                return (
                                <td
                                    key={dayIndex}
                                    data-dayindex={dayIndex}
                                    className={`todo-row-cell ${weeklyDragOverDay === dayIndex ? 'todo-row-cell-drag-over' : ''}`}
                                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                                    onDragEnter={() => setWeeklyDragOverDay(dayIndex)}
                                    onDragLeave={() => setWeeklyDragOverDay(null)}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        setWeeklyDragOverDay(null);
                                        if (weeklyDragTodoRef.current) {
                                            handleTodoDrop(weeklyDragTodoRef.current, cellDate);
                                            weeklyDragTodoRef.current = null;
                                        }
                                    }}
                                >
                                    {!targetUserId && (
                                        <button
                                            className="todo-row-add-btn"
                                            onClick={() => {
                                                setTodoDialog({ isOpen: true, dayIndex, dayDate: null });
                                                setTodoSubjectId('');
                                                setTodoDescription('');
                                            }}
                                            title={t('todo.add')}
                                        >+</button>
                                    )}
                                    <div className="todo-row-items">
                                        {(todosByDay[dayIndex] || []).map(todo => (
                                            <div
                                                key={todo.schedule_id}
                                                className={`todo-row-item ${todo.completed ? 'completed' : ''}`}
                                                draggable={!targetUserId}
                                                onDragStart={(e) => {
                                                    weeklyDragTodoRef.current = todo;
                                                    e.dataTransfer.effectAllowed = 'move';
                                                    e.dataTransfer.setData('text/plain', todo.schedule_id);
                                                    e.currentTarget.style.opacity = '0.5';
                                                }}
                                                onDragEnd={(e) => {
                                                    e.currentTarget.style.opacity = '1';
                                                    weeklyDragTodoRef.current = null;
                                                    setWeeklyDragOverDay(null);
                                                }}
                                                onTouchStart={(e) => handleWeeklyTodoTouchStart(e, todo)}
                                                onTouchEnd={() => {
                                                    if (weeklyTouchTimerRef.current) {
                                                        clearTimeout(weeklyTouchTimerRef.current);
                                                        weeklyTouchTimerRef.current = null;
                                                    }
                                                }}
                                                style={{
                                                    backgroundColor: todo.study_subject?.color || '#ccc',
                                                    color: getContrastColor(todo.study_subject?.color || '#ccc'),
                                                    '--todo-color': todo.study_subject?.color || '#1976d2',
                                                    cursor: !targetUserId ? 'grab' : 'default'
                                                }}
                                                onContextMenu={(e) => handleTodoContextMenu(e, todo)}
                                                onDoubleClick={(e) => handleTodoDoubleClick(e, todo)}
                                                title={`${todo.study_subject?.subjectname}${todo.special_text ? ' - ' + todo.special_text : ''}\n${t('timer.startTimer')}: ${t('edit.title')}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!todo.completed}
                                                    onChange={() => { if (!isReadOnly) toggleTodoCompleted(todo.schedule_id, !todo.completed); }}
                                                    disabled={isReadOnly}
                                                />
                                                <span className="todo-row-item-text">
                                                    {todo.special_text || todo.study_subject?.subjectname}
                                                    {todo.recurring_rule_id && <span className="recurring-icon" title={t('recurring.title')}>&#x1F504;</span>}
                                                    {todo.special_text && <span style={{ display: 'block', fontSize: '9px', opacity: 0.7 }}>({todo.study_subject?.subjectname})</span>}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </td>
                                );
                            })}
                        </tr>
                        {timeSlots.map((slot, rowIndex) => (
                            <tr key={rowIndex}>
                                {slot.isFirstHalf && (
                                    <td
                                        rowSpan="2"
                                        className="time-label-cell"
                                    >
                                        {slot.time}
                                    </td>
                                )}
                                {Array.from({ length: 7 }).map((_, dayIndex) => {
                                    const cellKey = `${dayIndex}-${rowIndex}`;
                                    const cellSchedules = scheduleMap[cellKey] || [];

                                    // Use ref for the first cell to measure height
                                    const isRefCell = rowIndex === 0 && dayIndex === 0;

                                    return (
                                        <td
                                            key={dayIndex}
                                            ref={isRefCell ? cellRef : null}
                                            className={`timetable-cell`}
                                            data-key={`${dayIndex}-${rowIndex}`}
                                            // style={{ ... }} Removed inline style, handled by CSS
                                            onDragOver={(e) => e.preventDefault()}
                                            useDrop="true"
                                            onDrop={(e) => handleDragEnd(e, rowIndex, dayIndex)}
                                        >
                                            {cellSchedules.map((schedule, idx) => {
                                                const heightPixels = (schedule.scheduled_time / 30) * cellHeight; // Use dynamic cellHeight
                                                return (
                                                    <div
                                                        key={schedule.schedule_id === -1 ? `img-${schedule.imageinary_schedule_id}` : schedule.schedule_id}
                                                        className={`schedule-bar ${editingScheduleId === (schedule.schedule_id !== -1 ? schedule.schedule_id : `img-${schedule.imageinary_schedule_id}`) ? 'active-edit' : ''}`}
                                                        draggable={!isReadOnly}
                                                        onDragStart={(e) => handleScheduleDragStart(e, schedule)}
                                                        onDrag={(e) => {
                                                            if (e.clientX !== 0 || e.clientY !== 0) {
                                                                setDragPosition({ x: e.clientX, y: e.clientY });
                                                            }
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (isReadOnly) return;
                                                            const id = schedule.schedule_id !== -1 ? schedule.schedule_id : `img-${schedule.imageinary_schedule_id}`;
                                                            setEditingScheduleId(prev => prev === id ? null : id);
                                                        }}
                                                        onTouchStart={(e) => {
                                                            if (isReadOnly) return;
                                                            const touch = e.touches[0];
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            const offsetX = touch.clientX - rect.left;
                                                            const offsetY = touch.clientY - rect.top;
                                                            setDragOffset({ x: offsetX, y: offsetY });
                                                            setDraggedSchedule(schedule);

                                                            // 탭 vs 드래그 구분을 위한 터치 시작 정보 기록
                                                            scheduleTouchRef.current = {
                                                                time: Date.now(),
                                                                x: touch.clientX,
                                                                y: touch.clientY,
                                                                schedule,
                                                                moved: false
                                                            };

                                                            // 태블릿용 선택 처리 루틴
                                                            const id = schedule.schedule_id !== -1 ? schedule.schedule_id : `img-${schedule.imageinary_schedule_id}`;
                                                            setEditingScheduleId(id);

                                                            // 아이패드 가시성 개선: 터치 시작 시 즉시 좌표 및 크기 설정
                                                            setDragPosition({ x: touch.clientX, y: touch.clientY });
                                                            setDragDimensions({ width: rect.width, height: rect.height });
                                                        }}
                                                        onTouchEnd={(e) => {
                                                            const ref = scheduleTouchRef.current;
                                                            if (!ref.schedule) return;
                                                            const touch = e.changedTouches[0];
                                                            const dt = Date.now() - ref.time;
                                                            const dist = Math.sqrt(
                                                                (touch.clientX - ref.x) ** 2 + (touch.clientY - ref.y) ** 2
                                                            );
                                                            const now = Date.now();
                                                            // 탭 판정: 300ms 미만 + 이동 10px 미만
                                                            if (dt < 300 && dist < 10) {
                                                                const last = lastTapRef.current;
                                                                const sinceLast = now - last.time;
                                                                const tapDist = Math.sqrt(
                                                                    (ref.x - last.x) ** 2 + (ref.y - last.y) ** 2
                                                                );
                                                                // 더블탭: 이전 탭과 400ms 이내 + 30px 이내
                                                                if (sinceLast < 400 && tapDist < 30) {
                                                                    e.preventDefault();
                                                                    setContextMenu({ x: ref.x, y: ref.y, schedule: ref.schedule });
                                                                    lastTapRef.current = { time: 0, x: 0, y: 0 };
                                                                } else {
                                                                    lastTapRef.current = { time: now, x: ref.x, y: ref.y };
                                                                }
                                                            }
                                                            scheduleTouchRef.current = { time: 0, x: 0, y: 0, schedule: null };
                                                        }}
                                                        onDragEnd={handleScheduleDragEnd}
                                                        style={(() => {
                                                            const sid = schedule.schedule_id !== -1 ? schedule.schedule_id : `img-${schedule.imageinary_schedule_id}`;
                                                            const ol = overlapLayout[sid] || { index: 0, count: 1 };
                                                            const totalWidth = showActualOverlay ? 38 : 80;
                                                            const baseLeft = showActualOverlay ? 2 : 10;
                                                            const colWidth = totalWidth / ol.count;
                                                            const colLeft = baseLeft + ol.index * colWidth;
                                                            return {
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: `${colLeft}%`,
                                                                width: `${colWidth}%`,
                                                                height: `${heightPixels}px`,
                                                                backgroundColor: schedule.study_subject.color,
                                                                color: getContrastColor(schedule.study_subject.color),
                                                                fontSize: '12px',
                                                                overflow: 'visible',
                                                                opacity: 1,
                                                                borderRadius: '4px',
                                                                border: editingScheduleId === sid ? '2px solid #2196F3' : '1px solid #444',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                zIndex: 10,
                                                                cursor: 'pointer',
                                                                touchAction: 'manipulation',
                                                                boxSizing: 'border-box'
                                                            };
                                                        })()}
                                                        onDoubleClick={(e) => handleContextMenu(e, schedule)}
                                                        title={`${schedule.study_subject.subjectname} (${schedule.scheduled_time} min)`}
                                                    >
                                                        <div
                                                            className="resize-handle resize-handle-top"
                                                            onMouseDown={(e) => handleResizeStart(e, schedule, 'top')}
                                                            onTouchStart={(e) => handleResizeStart(e, schedule, 'top')}
                                                        />
                                                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2, overflow: 'hidden' }}>
                                                            <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '100%', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                                <input type="checkbox" checked={!!schedule.completed} onChange={(e) => { e.stopPropagation(); if (!isReadOnly) toggleTodoCompleted(schedule.schedule_id, !schedule.completed); }} disabled={isReadOnly} style={{ cursor: isReadOnly ? 'default' : 'pointer', margin: 0, flexShrink: 0 }} />
                                                                {schedule.special_text || schedule.study_subject.subjectname}{schedule.recurring_rule_id && <span className="recurring-icon" title={t('recurring.title')}>&#x1F504;</span>}
                                                            </span>
                                                            {schedule.special_text && heightPixels >= 28 && <span style={{ fontSize: '9px', opacity: 0.7 }}>({schedule.study_subject.subjectname})</span>}
                                                            {showActualOverlay && heightPixels >= 28 && <span style={{ fontSize: '9px', opacity: 0.7 }}>({t('timer.planned')})</span>}
                                                        </span>
                                                        <div
                                                            className="resize-handle resize-handle-bottom"
                                                            onMouseDown={(e) => handleResizeStart(e, schedule, 'bottom')}
                                                            onTouchStart={(e) => handleResizeStart(e, schedule, 'bottom')}
                                                        />
                                                    </div>
                                                );
                                            })}
                                            {/* Actual session overlay */}
                                            {showActualOverlay && (actualSessionMap[cellKey] || []).map((session, oidx) => {
                                                const displaySeconds = session.modified_seconds != null ? session.modified_seconds : session.total_seconds;
                                                const overlayHeight = (displaySeconds / 1800) * cellHeight;
                                                const color = session.study_subject?.color || '#666';
                                                const mins = Math.floor(displaySeconds / 60);
                                                return (
                                                    <div
                                                        key={`overlay-${oidx}`}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: '42%',
                                                            width: '56%',
                                                            height: `${overlayHeight}px`,
                                                            background: `repeating-linear-gradient(45deg, ${getContrastColor(color) === 'white' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)'} 0px, ${getContrastColor(color) === 'white' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)'} 4px, transparent 4px, transparent 8px), ${color}`,
                                                            color: getContrastColor(color),
                                                            borderRadius: '4px',
                                                            border: '1px solid #444',
                                                            zIndex: 10,
                                                            pointerEvents: 'auto',
                                                            boxSizing: 'border-box',
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            fontSize: '11px',
                                                            overflow: 'hidden'
                                                        }}
                                                        title={`${t('timer.actual')}: ${session.study_subject?.subjectname || ''} (${mins}${t('minutes')})`}
                                                    >
                                                        {overlayHeight >= 20 && (
                                                            <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.2, overflow: 'hidden', padding: '0 2px' }}>
                                                                <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '100%' }}>{session.study_subject?.subjectname || ''}</span>
                                                                {overlayHeight >= 32 && <span style={{ fontSize: '9px', opacity: 0.7 }}>({t('timer.actual')})</span>}
                                                            </span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>}
            </div>{/* end printRef */}

            <Link to="/" className="back-button">{t('backToHome')}</Link>

            {/* Dialogs */}
            <SubjectPalette
                isOpen={isSubjectPaletteOpen}
                subjects={subjects}
                onClose={() => setIsSubjectPaletteOpen(false)}
                onAddSubject={() => setIsSubjectAddDialogOpen(true)}
                setSubjectInfo={setDraggedSubjectInfo}
                setDragPosition={setDragPosition}
                setDragDimensions={setDragDimensions}
            />

            <SubjectAddDialog
                isOpen={isSubjectAddDialogOpen}
                onClose={() => setIsSubjectAddDialogOpen(false)}
                onSave={(subject_name, category_id, subject_color, subject_unit_time) => {
                    createSubject(subject_name, category_id, subject_color, subject_unit_time);
                }}
                categories={categories}
                onCreateCategory={createCategory}
            />

            {/* Context Menu */}
            {
                contextMenu && (
                    <div
                        className="context-menu"
                        style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!contextMenu.schedule.completed && (
                            <div className="context-menu-item" onClick={handleStartTimer}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                                {t('timer.startTimer')}
                            </div>
                        )}
                        <div className="context-menu-item" onClick={handleEditSchedule}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                            {t('menu.edit')}
                        </div>
                        {contextMenu.schedule.recurring_rule_id && (
                            <div className="context-menu-item" onClick={handleDetachSchedule}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                    <polyline points="15 3 21 3 21 9"></polyline>
                                    <line x1="10" y1="14" x2="21" y2="3"></line>
                                </svg>
                                {t('recurring.edit.thisOnly')}
                            </div>
                        )}
                        <div className="context-menu-item" onClick={() => { toggleTodoCompleted(contextMenu.schedule.schedule_id, !contextMenu.schedule.completed); setContextMenu(null); }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            {contextMenu.schedule.completed ? t('todo.undo') : t('todo.complete')}
                        </div>
                        <div className="context-menu-item danger" onClick={handleDeleteSchedule}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                            {t('menu.delete')}
                        </div>
                    </div>
                )
            }

            <ConfirmationDialog
                isOpen={deleteDialog.isOpen}
                onClose={() => setDeleteDialog({ isOpen: false, schedule: null })}
                title={t('menu.delete')}
                message={deleteDialog.schedule ? t('deleteScheduleConfirm', { subject: deleteDialog.schedule.study_subject.subjectname }) : ''}
                onConfirm={confirmDeleteSchedule}
                onCancel={() => setDeleteDialog({ isOpen: false, schedule: null })}
                isDanger={true}
                confirmLabel={t('menu.delete')}
            />

            {/* Todo Context Menu */}
            {todoContextMenu && (
                <div
                    className="context-menu"
                    style={{ position: 'fixed', top: todoContextMenu.y, left: todoContextMenu.x }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {!todoContextMenu.schedule.completed && (
                        <div className="context-menu-item" onClick={handleTodoStartTimer}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                            </svg>
                            {t('timer.startTimer')}
                        </div>
                    )}
                    <div className="context-menu-item" onClick={handleTodoEdit}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        {t('todo.edit')}
                    </div>
                    {todoContextMenu.schedule.recurring_rule_id && (
                        <div className="context-menu-item" onClick={handleTodoDetach}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                <polyline points="15 3 21 3 21 9"></polyline>
                                <line x1="10" y1="14" x2="21" y2="3"></line>
                            </svg>
                            {t('recurring.edit.thisOnly')}
                        </div>
                    )}
                    <div className="context-menu-item danger" onClick={handleTodoDeleteAction}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                        {t('menu.delete')}
                    </div>
                </div>
            )}

            {/* Todo Delete Confirmation */}
            <ConfirmationDialog
                isOpen={todoDeleteDialog.isOpen}
                onClose={() => setTodoDeleteDialog({ isOpen: false, schedule: null })}
                title={t('menu.delete')}
                message={todoDeleteDialog.schedule ? t('todo.deleteTodoConfirm') : ''}
                onConfirm={confirmTodoDelete}
                onCancel={() => setTodoDeleteDialog({ isOpen: false, schedule: null })}
                isDanger={true}
                confirmLabel={t('menu.delete')}
            />

            {/* Todo Edit Dialog */}
            {todoEditDialog && (
                <div className="dialog-overlay">
                    <div className="dialog-box">
                        <h3 className="dialog-title">{t('todo.edit')}</h3>
                        <div>
                            <label className="dialog-label">{t('edit.subject')}</label>
                            <div className="todo-subject-list">
                                {subjects.map(s => (
                                    <div
                                        key={s.subject_id}
                                        className={`todo-subject-option ${String(todoEditDialog.subjectId) === String(s.subject_id) ? 'selected' : ''}`}
                                        onClick={() => setTodoEditDialog({ ...todoEditDialog, subjectId: s.subject_id })}
                                    >
                                        <span className="todo-subject-color" style={{ backgroundColor: s.color || '#ccc' }} />
                                        <span>{s.subjectname}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="dialog-label">{t('todo.addDescription')}</label>
                            <input
                                type="text"
                                value={todoEditDialog.specialText}
                                onChange={(e) => setTodoEditDialog({ ...todoEditDialog, specialText: e.target.value })}
                                className="dialog-input"
                                onKeyDown={(e) => e.key === 'Enter' && saveTodoEdit()}
                            />
                        </div>
                        <div>
                            <label className="dialog-label">{t('edit.start_time')}</label>
                            <input
                                type="date"
                                value={todoEditDialog.dateStr}
                                onChange={(e) => setTodoEditDialog({ ...todoEditDialog, dateStr: e.target.value })}
                                className="dialog-input"
                            />
                        </div>
                        <div className="dialog-actions">
                            <button className="btn-convert" onClick={convertTodoToSchedule}>{t('convert.toSchedule')}</button>
                            <div style={{ flex: 1 }} />
                            <button className="btn-secondary" onClick={() => setTodoEditDialog(null)}>{t('cancel')}</button>
                            <button className="btn-primary" onClick={saveTodoEdit}>{t('save')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Dialog */}
            {
                editDialog && (
                    <div className="dialog-overlay">
                        <div className="dialog-box">
                            <h3 className="dialog-title">{t('edit.title')}</h3>

                            <div>
                                <label className="dialog-label">{t('edit.subject')}</label>
                                <div className="todo-subject-list">
                                    {subjects.map(s => (
                                        <div
                                            key={s.subject_id}
                                            className={`todo-subject-option ${String(editDialog.subjectId) === String(s.subject_id) ? 'selected' : ''}`}
                                            onClick={() => setEditDialog({ ...editDialog, subjectId: s.subject_id })}
                                        >
                                            <span className="todo-subject-color" style={{ backgroundColor: s.color || '#ccc' }} />
                                            <span>{s.subjectname}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="dialog-label">{t('edit.subject_name')}</label>
                                <input
                                    type="text"
                                    value={editDialog.specialText}
                                    onChange={(e) => setEditDialog({ ...editDialog, specialText: e.target.value })}
                                    placeholder={subjects.find(s => s.subject_id === parseInt(editDialog.subjectId))?.subjectname || ''}
                                    className="dialog-input"
                                    onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                                />
                            </div>

                            <div>
                                <label className="dialog-label">{t('edit.start_time')}</label>
                                <input
                                    type="datetime-local"
                                    value={editDialog.startTimeLocal}
                                    onChange={(e) => setEditDialog({ ...editDialog, startTimeLocal: e.target.value })}
                                    className="dialog-input"
                                />
                            </div>

                            <div>
                                <label className="dialog-label">{t('edit.time')} ({t('minutes')})</label>
                                <input
                                    type="number"
                                    value={editDialog.time}
                                    onChange={(e) => setEditDialog({ ...editDialog, time: e.target.value })}
                                    className="dialog-input"
                                />
                            </div>

                            <div className="dialog-actions">
                                <button className="btn-convert" onClick={convertScheduleToTodo}>{t('convert.toTodo')}</button>
                                <div style={{ flex: 1 }} />
                                <button className="btn-secondary" onClick={() => setEditDialog(null)}>{t('cancel')}</button>
                                <button className="btn-primary" onClick={saveEdit}>{t('save')}</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ToDo Add Dialog */}
            {todoDialog.isOpen && (
                <div className="dialog-overlay">
                    <div className="dialog-box">
                        <h3 className="dialog-title">{t('todo.add')}</h3>
                        <div>
                            <label className="dialog-label">{t('todo.selectSubject')}</label>
                            <div className="todo-subject-list">
                                {subjects.map(s => (
                                    <div
                                        key={s.subject_id}
                                        className={`todo-subject-option ${String(todoSubjectId) === String(s.subject_id) ? 'selected' : ''}`}
                                        onClick={() => setTodoSubjectId(s.subject_id)}
                                    >
                                        <span className="todo-subject-color" style={{ backgroundColor: s.color || '#ccc' }} />
                                        <span>{s.subjectname}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="dialog-label">{t('todo.addDescription')}</label>
                            <input
                                type="text"
                                value={todoDescription}
                                onChange={(e) => setTodoDescription(e.target.value)}
                                className="dialog-input"
                                onKeyDown={(e) => e.key === 'Enter' && handleTodoAdd()}
                            />
                        </div>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => setTodoDialog({ isOpen: false, dayIndex: null, dayDate: null })}>{t('cancel')}</button>
                            <button className="btn-primary" onClick={handleTodoAdd} disabled={!todoSubjectId}>{t('save')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* PDF Preview Dialog */}
            {pdfPreview && (
                <div className="dialog-overlay" onClick={() => setPdfPreview(null)}>
                    <div className="pdf-preview-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3 className="dialog-title">{t('pdf.preview')}</h3>
                        <div className="pdf-preview-image-container">
                            <img
                                src={pdfPreview.imgDataUrl}
                                alt="PDF Preview"
                                className="pdf-preview-image"
                            />
                        </div>
                        <p className="pdf-preview-filename">{pdfPreview.fileName}</p>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => setPdfPreview(null)}>{t('cancel')}</button>
                            <button className="btn-primary" onClick={handlePdfSave}>{t('pdf.save')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* PNG Preview Dialog */}
            {pngPreview && (
                <div className="dialog-overlay" onClick={() => setPngPreview(null)}>
                    <div className="pdf-preview-dialog" onClick={(e) => e.stopPropagation()}>
                        <h3 className="dialog-title">{t('png.preview')}</h3>
                        <div className="pdf-preview-image-container">
                            <img
                                src={pngPreview.imgDataUrl}
                                alt="PNG Preview"
                                className="pdf-preview-image"
                            />
                        </div>
                        <p className="pdf-preview-filename">{pngPreview.fileName}</p>
                        <div className="dialog-actions">
                            <button className="btn-secondary" onClick={() => setPngPreview(null)}>{t('cancel')}</button>
                            <button className="btn-primary" onClick={handlePngSave}>{t('png.save')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Drag Preview (Touch & Mouse) */}
            <DragPreview
                subject={draggedSubjectInfo || (draggedSchedule ? draggedSchedule.study_subject : null)}
                position={dragPosition}
                offset={draggedSchedule ? dragOffset : null}
                dimensions={dragDimensions}
                customStyle={draggedSubjectInfo ? {
                    padding: '5px',
                    justifyContent: 'flex-start',
                    fontSize: '12px',
                    fontWeight: 'bold',
                } : {
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'normal',
                    padding: 0,
                    border: '1px solid #444',
                }}
            >
                {draggedSubjectInfo ? draggedSubjectInfo.subjectname : (draggedSchedule ? (draggedSchedule.special_text || draggedSchedule.study_subject.subjectname) : '')}
            </DragPreview>
            <ShareDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                cardType="timetable"
                data={shareData}
            />
        </div >
    );
}

export default TimeTable;